import type { LayoutResizeHandle } from "~/v3-poc/features/workspace/shared/layoutSettings/layoutSettings.types";
import type { RglSettingId } from "./reactGridLayoutSettings.schema";
import type { ReactGridLayoutSettings } from "./reactGridLayoutSettings.types";

const HANDLE_ORDER: readonly LayoutResizeHandle[] = [
  "n",
  "ne",
  "e",
  "se",
  "s",
  "sw",
  "w",
  "nw",
];

const HANDLE_VALUES = new Set<LayoutResizeHandle>(HANDLE_ORDER);

export const readRglNumeric = (
  c: ReactGridLayoutSettings,
  id: RglSettingId,
): number => {
  switch (id) {
    case "cols":
      return c.cols;
    case "rowHeight":
      return c.rowHeight;
    case "marginX":
      return c.marginX;
    case "marginY":
      return c.marginY;
    case "containerPaddingX":
      return c.containerPaddingX;
    case "containerPaddingY":
      return c.containerPaddingY;
    case "maxRows":
      return c.maxRows.value;
    case "dragThreshold":
      return c.dragThreshold;
    case "positionScale":
      return c.positionScale;
    case "globalMinW":
      return c.globalMin.w;
    case "globalMinH":
      return c.globalMin.h;
    case "globalMaxW":
      return c.globalMax.w;
    case "globalMaxH":
      return c.globalMax.h;
    case "snapX":
      return c.snap.x;
    case "snapY":
      return c.snap.y;
    case "aspectRatio":
      return c.aspect.ratio;
    case "externalDropW":
      return c.externalDrop.w;
    case "externalDropH":
      return c.externalDrop.h;
    default:
      throw new Error(`readNumeric called on non-numeric field: ${id}`);
  }
};

export const writeRglNumeric = (
  c: ReactGridLayoutSettings,
  id: RglSettingId,
  value: number,
): ReactGridLayoutSettings => {
  switch (id) {
    case "cols":
      return { ...c, cols: value };
    case "rowHeight":
      return { ...c, rowHeight: value };
    case "marginX":
      return { ...c, marginX: value };
    case "marginY":
      return { ...c, marginY: value };
    case "containerPaddingX":
      return { ...c, containerPaddingX: value };
    case "containerPaddingY":
      return { ...c, containerPaddingY: value };
    case "maxRows":
      return { ...c, maxRows: { ...c.maxRows, value } };
    case "dragThreshold":
      return { ...c, dragThreshold: value };
    case "positionScale":
      return { ...c, positionScale: value };
    case "globalMinW":
      return { ...c, globalMin: { ...c.globalMin, w: value } };
    case "globalMinH":
      return { ...c, globalMin: { ...c.globalMin, h: value } };
    case "globalMaxW":
      return { ...c, globalMax: { ...c.globalMax, w: value } };
    case "globalMaxH":
      return { ...c, globalMax: { ...c.globalMax, h: value } };
    case "snapX":
      return { ...c, snap: { ...c.snap, x: value } };
    case "snapY":
      return { ...c, snap: { ...c.snap, y: value } };
    case "aspectRatio":
      return { ...c, aspect: { ...c.aspect, ratio: value } };
    case "externalDropW":
      return { ...c, externalDrop: { ...c.externalDrop, w: value } };
    case "externalDropH":
      return { ...c, externalDrop: { ...c.externalDrop, h: value } };
    default:
      throw new Error(`writeNumeric called on non-numeric field: ${id}`);
  }
};

export const writeRglBoolean = (
  c: ReactGridLayoutSettings,
  id: RglSettingId,
  value: boolean,
): ReactGridLayoutSettings => {
  switch (id) {
    case "maxRowsLimited":
      return { ...c, maxRows: { ...c.maxRows, limited: value } };
    case "autoSize":
      return { ...c, autoSize: value };
    case "bounded":
      return { ...c, bounded: value };
    case "gridBounds":
      return { ...c, gridBounds: value };
    case "containerBounds":
      return { ...c, containerBounds: value };
    case "boundedX":
      return { ...c, boundedX: value };
    case "boundedY":
      return { ...c, boundedY: value };
    case "minMaxSize":
      return { ...c, minMaxSize: value };
    case "globalMinEnabled":
      return { ...c, globalMin: { ...c.globalMin, enabled: value } };
    case "globalMaxEnabled":
      return { ...c, globalMax: { ...c.globalMax, enabled: value } };
    case "snapEnabled":
      return { ...c, snap: { ...c.snap, enabled: value } };
    case "aspectEnabled":
      return { ...c, aspect: { ...c.aspect, enabled: value } };
    // `externalDropEnabled` is unreachable here: its schema status is
    // "unavailable" and the reducer guard drops the change before dispatch.
    default:
      return c;
  }
};

const asMember = <T extends string>(
  value: string,
  members: ReadonlyArray<T>,
): T | undefined =>
  (members as ReadonlyArray<string>).includes(value) ? (value as T) : undefined;

const CONTAINER_PADDING_MODES = [
  "margin",
  "custom",
] as const satisfies ReadonlyArray<
  ReactGridLayoutSettings["containerPaddingMode"]
>;
const COMPACTORS = [
  "vertical",
  "horizontal",
  "none",
] as const satisfies ReadonlyArray<ReactGridLayoutSettings["compactor"]>;
const POSITION_STRATEGIES = [
  "absolute",
  "transform",
  "scaled",
] as const satisfies ReadonlyArray<ReactGridLayoutSettings["positionStrategy"]>;

export const writeRglSelect = (
  c: ReactGridLayoutSettings,
  id: RglSettingId,
  value: string,
): ReactGridLayoutSettings => {
  switch (id) {
    case "containerPaddingMode": {
      const next = asMember(value, CONTAINER_PADDING_MODES);
      return next ? { ...c, containerPaddingMode: next } : c;
    }
    case "compactor": {
      const next = asMember(value, COMPACTORS);
      return next ? { ...c, compactor: next } : c;
    }
    case "positionStrategy": {
      const next = asMember(value, POSITION_STRATEGIES);
      return next ? { ...c, positionStrategy: next } : c;
    }
    default:
      return c;
  }
};

export const isRglResizeHandle = (value: string): value is LayoutResizeHandle =>
  HANDLE_VALUES.has(value as LayoutResizeHandle);

export const writeRglResizeHandle = (
  current: ReadonlyArray<LayoutResizeHandle>,
  handle: LayoutResizeHandle,
  checked: boolean,
): ReadonlyArray<LayoutResizeHandle> => {
  const set = new Set(current);
  if (checked) {
    set.add(handle);
  } else {
    set.delete(handle);
  }
  return HANDLE_ORDER.filter((h) => set.has(h));
};
